import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { FHButton, FHConfirmDialog, FHSignatureDialog, FProgressBar } from '../../components/base';
import {
  DeliveryMethods,
  EvvQrDialog,
  getActiveBayAssignmentAtDateTime,
  GuardianSignatureMessage,
  ReviewAnchor,
  SegmentSlider,
  SessionInfo,
  SessionReadOnlyView,
} from './btSessionNotes';
import {
  clientActions,
  providerAssignmentActions,
  sessionActions,
  cohortActions,
} from '../../store';
import PageContainer from '../../components/layout/PageContainer';
import ReviewDialog from '../../components/layout/qa/review-dialog';
import { history, areUserRolesIn } from '../../helpers';
import { getUtcOffset } from '../../utils/common';
import { Roles, BTSessionStatus, ClinicalRelationships, BAYS } from '../../constants';
import { CQAReviewStatus } from '../../components/layout/qa/constants';
import { useSessionEditable } from '../../hooks';
import { useEvvQrDialogPoll, useSessionAcknowledgementGuard } from './hooks';
import { EVV_TOKEN_KINDS } from './constants';
import { buildEvvVerificationRoute, isMobileChrome } from './utils';

import type { RootState } from '../../store';
import type { EvvQrDialogPresentationState } from './btSessionNotes/types';

function BTSession() {
  const { id: sessionId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('sessionNotes');
  const { user, loading: authLoading } = useSelector((state: RootState) => state.auth);
  const { loading: clientsLoading } = useSelector((state: RootState) => state.clients);
  const {
    current: session,
    reviewLoading,
    loading: sessionLoading,
  } = useSelector((state: RootState) => state.sessions);

  const { provider } = useSelector((state: RootState) => state.providers);
  const { clientCohortAssignments } = useSelector((state: RootState) => state.cohorts);

  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  const [updateDisabled, setUpdateDisabled] = useState<boolean>(false);
  const [reviewDialogOpenedOnce, setReviewDialogOpenedOnce] = useState<boolean>(false);
  const [openGuardianSignDialog, setOpenGuardianSignDialog] = useState<boolean>(false);
  const [openSessionNotAcknowledgedDialog, setOpenSessionNotAcknowledgedDialog] =
    useState<boolean>(false);
  const [reviewAnchorVisible, setReviewAnchorVisible] = useState<boolean>(false);
  const [showConfirmQaReview, setShowConfirmQaReview] = useState<boolean>(false);
  const [submitQaReviewBtnLoading, setSubmitQaReviewBtnLoading] = useState<boolean>(false);
  const [isSessionStartLocked, setIsSessionStartLocked] = useState<boolean>(false);
  const [evvQrDialog, setEvvQrDialog] = useState<EvvQrDialogPresentationState | null>(null);
  const initialSessionIdRef = useRef(sessionId);
  const evvRecoverySessionIdRef = useRef<number | string | null>(null);

  const [isVBT, isPRBT] = useMemo(() => {
    return [areUserRolesIn(user?.roles, [Roles.Vbt]), areUserRolesIn(user?.roles, [Roles.Prbt])];
  }, [user]);

  const isBay7Active = useMemo(() => {
    return getActiveBayAssignmentAtDateTime(
      clientCohortAssignments,
      BAYS.BAY_7,
      session?.date_start ?? null,
      session?.date_start ?? null,
    )?.cohort?.name?.toUpperCase() === BAYS.BAY_7
      ? true
      : false;
  }, [clientCohortAssignments, session]);

  const isBay7InPersonGuardianFlow = useMemo(
    () => isBay7Active && session?.delivery_method === DeliveryMethods.IN_PERSON,
    [isBay7Active, session?.delivery_method],
  );
  const isEditable = useSessionEditable(isBay7InPersonGuardianFlow);
  const isStartEvvPending = Boolean(
    session?.id && session.evv_required && !session.evv_start_captured,
  );

  const checkForCQAReview = useCallback(() => {
    const openReviewDialog =
      (areUserRolesIn(user?.roles, [Roles.QaSpecialist]) &&
        session?.status !== BTSessionStatus.Started &&
        session?.status !== BTSessionStatus.AwaitingGuardianSignature) ||
      (areUserRolesIn(user?.roles, [
        Roles.Prbt,
        Roles.Admin,
        Roles.ClientSuccessManager,
        Roles.Biller,
        Roles.InvoiceCoordinator,
        Roles.Vbt,
      ]) &&
        session?.status === BTSessionStatus.EditsRequested);
    setReviewAnchorVisible(openReviewDialog);
  }, [user, session?.id]);

  useEffect(() => {
    dispatch(sessionActions.clearSession());
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(sessionActions.clearSession());
      dispatch(providerAssignmentActions.clearAssignment());
      dispatch(cohortActions.clearClientCohortState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [ClinicalRelationships.HomeBcba, ClinicalRelationships.BobbingBcba],
      }),
    );
  }, [provider?.id]);

  useEffect(() => {
    if (!sessionId) {
      dispatch(clientActions.getList({ getAccessTokenSilently }));
      dispatch(sessionActions.clearSession());
      return;
    }

    (async function () {
      const response = await dispatch(
        sessionActions.getSession({ sessionId, getAccessTokenSilently }),
      );
      if (response.error) {
        history.navigate('/new-session');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (session?.status === BTSessionStatus.EditsRequested && !reviewDialogOpenedOnce) {
      setReviewDialogOpen(true);
      setReviewDialogOpenedOnce(true);
    }
  }, [session, reviewDialogOpenedOnce]);

  useEffect(() => {
    if (
      isEditable &&
      (areUserRolesIn(user?.roles, [Roles.Prbt]) ||
        (areUserRolesIn(user?.roles, [Roles.Vbt]) && isBay7Active)) &&
      session?.status === BTSessionStatus.AwaitingGuardianSignature
    ) {
      setUpdateDisabled(true);
    } else {
      setUpdateDisabled(false);
    }
  }, [isEditable, session, isBay7Active]);

  useSessionAcknowledgementGuard({
    session,
    user,
    sessionId,
    checkForCQAReview,
    setOpenSessionNotAcknowledgedDialog,
  });

  const handleStartEvvCaptured = useCallback(
    async (evvSessionId: number | string) => {
      setEvvQrDialog(null);
      evvRecoverySessionIdRef.current = null;
      window.history.replaceState(null, '', `/sessions/${evvSessionId}`);
      await dispatch(
        sessionActions.getSession({
          sessionId: evvSessionId,
          getAccessTokenSilently,
        }),
      );
      setIsSessionStartLocked(false);
    },
    [dispatch, getAccessTokenSilently],
  );

  const handleCancelPendingStartEvv = useCallback(() => {
    setEvvQrDialog(null);
    setIsSessionStartLocked(true);
  }, []);

  useEffect(() => {
    if (!isStartEvvPending || !session?.id) {
      evvRecoverySessionIdRef.current = null;
      return;
    }

    setIsSessionStartLocked(true);

    if (String(initialSessionIdRef.current) !== String(session.id)) return;
    if (
      evvQrDialog?.kind === EVV_TOKEN_KINDS.START &&
      String(evvQrDialog.sessionId) === String(session.id)
    ) {
      return;
    }
    if (String(evvRecoverySessionIdRef.current) === String(session.id)) return;

    const evvSessionId = session.id;
    evvRecoverySessionIdRef.current = evvSessionId;

    void (async () => {
      const response = await dispatch(
        sessionActions.createStartEvvToken({
          sessionId: evvSessionId,
          replaceActive: true,
          getAccessTokenSilently,
        }),
      );
      if (response.error) return;

      const route = buildEvvVerificationRoute({
        token: response.payload.token,
        kind: EVV_TOKEN_KINDS.START,
        sessionId: evvSessionId,
        returnToSession: isMobileChrome(),
      });

      if (isMobileChrome()) {
        history.navigate(route);
        return;
      }

      setEvvQrDialog({
        kind: EVV_TOKEN_KINDS.START,
        route,
        sessionId: evvSessionId,
        onClose: handleCancelPendingStartEvv,
        onCaptured: () => {
          void handleStartEvvCaptured(evvSessionId);
        },
      });
    })();
  }, [
    dispatch,
    evvQrDialog?.kind,
    evvQrDialog?.sessionId,
    getAccessTokenSilently,
    handleCancelPendingStartEvv,
    handleStartEvvCaptured,
    isStartEvvPending,
    session?.id,
  ]);

  useEvvQrDialogPoll({
    dialog: evvQrDialog,
    onTokenRefreshed: (route) => setEvvQrDialog((dialog) => dialog && { ...dialog, route }),
    onCaptured: () => {
      setEvvQrDialog(null);
      void evvQrDialog?.onCaptured();
    },
  });

  const handleOpenReviewDialog = async () => {
    if (!sessionId || !session) return;

    setReviewDialogOpen(true);
    if (session?.status === BTSessionStatus.AwaitingReview && !session.clinical_qa_review) {
      setSubmitQaReviewBtnLoading(true);
      await dispatch(
        sessionActions.createCQAReview({
          sessionId,
          data: {
            date_start: new Date(),
            utc_offset_date_start: getUtcOffset(moment(new Date())),
          },
          getAccessTokenSilently,
        }),
      );
      setSubmitQaReviewBtnLoading(false);
      setShowConfirmQaReview(false);
    }
  };

  const handleConfirmQAReview = () => {
    if (session?.status === BTSessionStatus.AwaitingReview && !session.clinical_qa_review) {
      setShowConfirmQaReview(true);
    } else {
      if (!sessionId || !session) return;
      setReviewDialogOpen(true);
    }
  };

  const handleParentSignature = async (dataURL: string) => {
    if (!session) return;
    const guardianSignatureDate = moment(new Date());
    const data = {
      guardian_provider_signature: dataURL,
      date_guardian_provider_signed: guardianSignatureDate,
      utc_offset_date_guardian_provider_signed: getUtcOffset(guardianSignatureDate),
    };

    // Save guardian signature
    const res = await dispatch(
      sessionActions.saveGuardianSignartures({
        sessionId: session.id,
        data,
        getAccessTokenSilently,
      }),
    );

    if (res.error) {
      return;
    }
    setOpenGuardianSignDialog(false);
    history.navigate('/dashboard');
  };

  if (authLoading || clientsLoading || (sessionLoading && !session?.id)) return <FProgressBar />;

  return (
    <PageContainer sx={{ px: 3, pt: 3 }} dataTestId="sessionNotePageContainer">
      {sessionLoading && <FProgressBar />}
      {isEditable || !session?.id ? (
        <Box>
          <SessionInfo
            isEditable={isEditable && !updateDisabled}
            setIsSessionStartLocked={setIsSessionStartLocked}
            setEvvQrDialog={setEvvQrDialog}
          />
          {!isSessionStartLocked && !isStartEvvPending && (
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
              <SegmentSlider
                isBay7Active={isBay7Active}
                isEditable={isEditable}
                updateDisabled={updateDisabled}
                isVBT={isVBT}
                isPRBT={isPRBT}
                isBay7InPersonGuardianFlow={isBay7InPersonGuardianFlow}
              />
            </Box>
          )}
        </Box>
      ) : (
        <SessionReadOnlyView />
      )}
      {areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
        session?.status === BTSessionStatus.Completed &&
        session.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FHButton
              sx={{ mb: 5, width: '150px' }}
              onClick={() => {
                history.navigate(`/sessions/${session?.id}/review`);
              }}
            >
              Acknowledge
            </FHButton>
          </Box>
        )}
      {areUserRolesIn(user?.roles, [Roles.Guardian]) &&
        session?.status === BTSessionStatus.AwaitingGuardianSignature && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FHButton
              sx={{ mb: 5, width: '100px' }}
              onClick={() => setOpenGuardianSignDialog(true)}
            >
              {t('sign')}
            </FHButton>
          </Box>
        )}

      {evvQrDialog && (
        <EvvQrDialog
          open
          kind={evvQrDialog.kind}
          route={evvQrDialog.route}
          sessionId={evvQrDialog.sessionId}
          onClose={evvQrDialog.onClose}
        />
      )}

      {reviewAnchorVisible && (
        <ReviewAnchor disabled={reviewLoading} onOpen={handleConfirmQAReview} />
      )}
      {reviewAnchorVisible && session?.clinical_qa_review && (
        <>
          <ReviewDialog
            open={reviewDialogOpen}
            clinicalQAReview={session?.clinical_qa_review}
            sessionStatus={session?.status}
            onClose={() => setReviewDialogOpen(false)}
          />
        </>
      )}
      <FHSignatureDialog
        headerText={t('parentSignature.signature')}
        cancelText={t('parentSignature.clearSignature')}
        submitText={t('parentSignature.submit')}
        consentText={t('parentSignature.consentMessage')}
        open={openGuardianSignDialog}
        onClose={() => setOpenGuardianSignDialog(false)}
        onSign={(dataUrl) => handleParentSignature(dataUrl)}
        contentRenderer={() => GuardianSignatureMessage(t)}
      />
      <FHConfirmDialog
        open={openSessionNotAcknowledgedDialog}
        content={
          session?.status === BTSessionStatus.Completed
            ? 'Please acknowledge the session notes review.'
            : 'Please acknowledge the session notes review before making edits to the session notes.'
        }
        footerContainerStyles={{ justifyContent: 'center' }}
        submittext={'Go to acknowledgment'}
        hideCancelButton
        closeIconDataTestId={'closeAcknowledgementDialogeBox'}
        onClose={() => {
          setOpenSessionNotAcknowledgedDialog(false);
          checkForCQAReview();
        }}
        onSubmit={() => {
          if (!session?.id) return;
          history.navigate(`/sessions/${session?.id}/review`);
        }}
      />
      <FHConfirmDialog
        open={showConfirmQaReview}
        submittext="Continue"
        canceltext="Cancel"
        content="To review and grade this session note, select Continue, and a review will be created. If you do not want to review this session note, select Cancel."
        onClose={() => setShowConfirmQaReview(false)}
        submitLoading={submitQaReviewBtnLoading}
        onSubmit={() => {
          handleOpenReviewDialog();
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        dialogStyles={{ padding: '16px 24px', height: '248px' }}
        submitBtnTestId={'confirmQaReviewDialogButton'}
      />
    </PageContainer>
  );
}

export default BTSession;
